/**
 * ContactsPage - Manage studio contacts
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getContacts,
  createContact,
  updateContact,
  deleteContact,
  type Contact,
  type CreateContactData,
  type UpdateContactData,
  getContactFullName,
} from '../../api/studios';
import { DashboardMobileNav } from '../../components/dashboard';

export default function ContactsPage() {
  const { user, logout } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getContacts();
      setContacts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contacts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveContact = async (data: CreateContactData | UpdateContactData) => {
    try {
      setIsSaving(true);
      setError(null);

      if (editingContact) {
        const updated = await updateContact(editingContact.id, data);
        setContacts((prev) =>
          prev.map((c) => (c.id === editingContact.id ? updated : c))
        );
        setEditingContact(null);
      } else {
        const created = await createContact(data as CreateContactData);
        setContacts((prev) => [...prev, created]);
        setIsAddingContact(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save contact');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    try {
      setError(null);
      await deleteContact(contactId);
      setContacts((prev) => prev.filter((c) => c.id !== contactId));
      setDeleteConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete contact');
    }
  };

  const primaryContact = contacts.find((c) => c.is_primary);
  const otherContacts = contacts.filter((c) => !c.is_primary);

  return (
    <div className="min-h-screen bg-warm-50">
      {/* Header */}
      <header className="bg-white border-b border-warm-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logo.png" alt="Stage Door Access" className="h-7" />
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-warm-600 hover:text-warm-900 text-sm">
                Dashboard
              </Link>
              <Link to="/dashboard/bookings" className="text-warm-600 hover:text-warm-900 text-sm">
                My Bookings
              </Link>
              <Link to="/dashboard/inquiries" className="text-warm-600 hover:text-warm-900 text-sm">
                My Inquiries
              </Link>
              <Link to="/dashboard/profile" className="text-warm-600 hover:text-warm-900 text-sm">
                Studio Profile
              </Link>
              <Link to="/dashboard/contacts" className="text-primary-600 font-medium text-sm">
                Contacts
              </Link>
            </nav>

            <div className="flex items-center gap-4">
              <span className="text-warm-600 text-sm hidden sm:block">{user?.email}</span>
              <button
                onClick={() => logout()}
                className="text-warm-600 hover:text-warm-900 text-sm font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      <DashboardMobileNav />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="hidden sm:block mb-6">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <Link to="/dashboard" className="text-primary-600 hover:text-primary-700">
                Dashboard
              </Link>
            </li>
            <li className="text-warm-400">/</li>
            <li className="text-warm-600">Contacts</li>
          </ol>
        </nav>

        <div className="flex items-center justify-between gap-3 mb-5 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900 mb-2">
              Studio Contacts
            </h1>
            <p className="hidden sm:block text-warm-600">
              Manage contacts who receive booking notifications and marketing emails.
            </p>
          </div>

          <button
            onClick={() => setIsAddingContact(true)}
            className="btn-primary flex items-center gap-2"
            disabled={isAddingContact}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span className="hidden sm:inline">Add Contact</span>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-danger-700">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-danger-600 hover:text-danger-700"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Add Contact Form */}
        {isAddingContact && (
          <div className="mb-6">
            <ContactForm
              onSave={handleSaveContact}
              onCancel={() => setIsAddingContact(false)}
              isSaving={isSaving}
            />
          </div>
        )}

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-20 bg-warm-100 rounded" />
              </div>
            ))}
          </div>
        ) : contacts.length === 0 ? (
          <div className="card text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
            <h3 className="font-semibold text-warm-900 mb-2">No contacts yet</h3>
            <p className="text-warm-500 mb-4">Add contacts to receive booking notifications.</p>
            <button onClick={() => setIsAddingContact(true)} className="btn-primary">
              Add Your First Contact
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Primary Contact */}
            {primaryContact && (
              <div className="mb-8">
                <h2 className="text-sm font-medium text-warm-500 uppercase tracking-wide mb-3">
                  Primary Contact
                </h2>
                {editingContact?.id === primaryContact.id ? (
                  <ContactForm
                    contact={primaryContact}
                    onSave={handleSaveContact}
                    onCancel={() => setEditingContact(null)}
                    isSaving={isSaving}
                  />
                ) : (
                  <ContactCard
                    contact={primaryContact}
                    onEdit={() => setEditingContact(primaryContact)}
                    onDelete={() => setDeleteConfirm(primaryContact.id)}
                    isPrimary
                  />
                )}
              </div>
            )}

            {/* Other Contacts */}
            {otherContacts.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-warm-500 uppercase tracking-wide mb-3">
                  Additional Contacts
                </h2>
                <div className="space-y-4">
                  {otherContacts.map((contact) =>
                    editingContact?.id === contact.id ? (
                      <ContactForm
                        key={contact.id}
                        contact={contact}
                        onSave={handleSaveContact}
                        onCancel={() => setEditingContact(null)}
                        isSaving={isSaving}
                      />
                    ) : (
                      <ContactCard
                        key={contact.id}
                        contact={contact}
                        onEdit={() => setEditingContact(contact)}
                        onDelete={() => setDeleteConfirm(contact.id)}
                      />
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-warm-900 mb-2">Delete Contact?</h3>
              <p className="text-warm-600 mb-6">
                This will permanently remove this contact from your studio. They will no longer
                receive any notifications or emails.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="btn py-2"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteContact(deleteConfirm)}
                  className="btn bg-danger-600 text-white hover:bg-danger-700 py-2"
                >
                  Delete Contact
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// =============================================================================
// Contact Card Component
// =============================================================================

interface ContactCardProps {
  contact: Contact;
  onEdit: () => void;
  onDelete: () => void;
  isPrimary?: boolean;
}

function ContactCard({ contact, onEdit, onDelete, isPrimary }: ContactCardProps) {
  return (
    <div className={`card ${isPrimary ? 'border-2 border-primary-200' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-semibold text-primary-700">
              {contact.first_name[0]}
              {contact.last_name[0]}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-warm-900">
                {getContactFullName(contact)}
              </h3>
              {isPrimary && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">
                  Primary
                </span>
              )}
            </div>
            {contact.title && (
              <p className="text-sm text-warm-500">{contact.title}</p>
            )}
            <div className="mt-2 space-y-1">
              <p className="text-sm text-warm-600 flex items-center gap-2">
                <svg className="w-4 h-4 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {contact.email}
              </p>
              {contact.phone && (
                <p className="text-sm text-warm-600 flex items-center gap-2">
                  <svg className="w-4 h-4 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {contact.phone}
                </p>
              )}
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs">
              {contact.receives_booking_emails && (
                <span className="flex items-center gap-1 text-success-600">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Booking Emails
                </span>
              )}
              {contact.receives_marketing_emails && (
                <span className="flex items-center gap-1 text-success-600">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Marketing
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="p-2 text-warm-400 hover:text-warm-600 rounded-lg hover:bg-warm-100"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          {!isPrimary && (
            <button
              onClick={onDelete}
              className="p-2 text-warm-400 hover:text-danger-600 rounded-lg hover:bg-danger-50"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Contact Form Component
// =============================================================================

interface ContactFormProps {
  contact?: Contact;
  onSave: (data: CreateContactData | UpdateContactData) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function ContactForm({ contact, onSave, onCancel, isSaving }: ContactFormProps) {
  const [formData, setFormData] = useState({
    first_name: contact?.first_name || '',
    last_name: contact?.last_name || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    title: contact?.title || '',
    is_primary: contact?.is_primary || false,
    receives_booking_emails: contact?.receives_booking_emails ?? true,
    receives_marketing_emails: contact?.receives_marketing_emails ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      phone: formData.phone || null,
      title: formData.title || null,
      is_primary: formData.is_primary,
      receives_booking_emails: formData.receives_booking_emails,
      receives_marketing_emails: formData.receives_marketing_emails,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="card">
      <h3 className="text-lg font-semibold text-warm-900 mb-4">
        {contact ? 'Edit Contact' : 'Add New Contact'}
      </h3>

      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="first_name" className="label">
              First Name *
            </label>
            <input
              id="first_name"
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData((f) => ({ ...f, first_name: e.target.value }))}
              className="input w-full"
              required
            />
          </div>
          <div>
            <label htmlFor="last_name" className="label">
              Last Name *
            </label>
            <input
              id="last_name"
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData((f) => ({ ...f, last_name: e.target.value }))}
              className="input w-full"
              required
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="email" className="label">
              Email *
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
              className="input w-full"
              required
            />
          </div>
          <div>
            <label htmlFor="phone" className="label">
              Phone
            </label>
            <input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))}
              className="input w-full"
            />
          </div>
        </div>

        <div>
          <label htmlFor="title" className="label">
            Title
          </label>
          <input
            id="title"
            type="text"
            value={formData.title}
            onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))}
            className="input w-full"
            placeholder="e.g., Studio Owner, Office Manager"
          />
        </div>

        <div className="pt-2 space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.receives_booking_emails}
              onChange={(e) =>
                setFormData((f) => ({ ...f, receives_booking_emails: e.target.checked }))
              }
              className="w-4 h-4 text-primary-600 border-warm-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-warm-700">Receive booking notifications</span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.receives_marketing_emails}
              onChange={(e) =>
                setFormData((f) => ({ ...f, receives_marketing_emails: e.target.checked }))
              }
              className="w-4 h-4 text-primary-600 border-warm-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-warm-700">Receive marketing emails about new classes</span>
          </label>

          {!contact?.is_primary && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_primary}
                onChange={(e) => setFormData((f) => ({ ...f, is_primary: e.target.checked }))}
                className="w-4 h-4 text-primary-600 border-warm-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-warm-700">Set as primary contact</span>
            </label>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-warm-200">
        <button type="button" onClick={onCancel} className="btn py-2" disabled={isSaving}>
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary py-2 disabled:opacity-50"
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : contact ? 'Save Changes' : 'Add Contact'}
        </button>
      </div>
    </form>
  );
}
